import { Dictionary } from "@/types/base";

// indexDB have not implement
export type StorageMode = "session" | "local" | "s&l" | "indexDB";
export type StorageOptions = {
  mode: StorageMode;
  name: string;
  autoFormat?: boolean;
  scope?: string;
  useScope?: boolean;
};

class Storage {
  // 顺带可以做一些自动JSON序列化的事情或者加作用于的事情等等，后面在扩展
  static isDebug = false;
  static trace = false;
  constructor(public options: StorageOptions) {
    this.optionsCheck(options);
  }

  // attrs
  get mode() {
    return this.options.mode;
  }

  get name() {
    return this.options.name;
  }

  get scope() {
    return this.options.scope;
  }

  get useScope() {
    return this.options.useScope ?? false;
  }

  get autoFormat() {
    return this.options.autoFormat ?? false;
  }

  // private utils methods

  private optionsCheck(options: StorageOptions) {
    // do some options check
    if (options.useScope) {
      if (!options.scope) throw Error("when useScope, you should provide a scope");
    }
  }

  private valueFormatter(v: any, parse = false) {
    let result = v;
    if (this.autoFormat) {
      if (parse) {
        try {
          result = JSON.parse(v);
        } catch (e) {
          result = v;
        }
      } else {
        if (typeof v === "object") {
          try {
            result = JSON.stringify(v);
          } catch (e) {
            console.log(`${this.name}: ${e}`);
            result = v;
          }
        }
      }
    }
    return result;
  }

  private keyFormatter(key: string | number, scope = this.scope): string {
    const stringKey = String(key);
    if (this.useScope) {
      return `${scope}/${stringKey}`;
    }
    return stringKey;
  }

  private log(msg: string) {
    if (Storage.isDebug) {
      console.group(`%c[${this.name} trace]`, "color: blue");
      Storage.trace ? console.trace(msg) : console.log(msg);
      console.groupEnd();
    }
  }

  // api

  /**
   * 后面接入indexDB,这里可能是异步的，或者到时候重写一个方法
   * 正常情况不影响
   * @param key 键
   * @param v 值
   * @param customScope 用来临时区别已经设置好的scope, 不填就用已经设置好的
   */
  save(key: string, v: string, customScope = this.scope) {
    this.log(`save: ${key}`);
    const formattedKey = this.keyFormatter(key, customScope);
    const formattedVal = this.valueFormatter(v);
    switch (this.mode) {
      case "session":
        sessionStorage.setItem(formattedKey, formattedVal);
        break;
      case "local":
        localStorage.setItem(formattedKey, formattedVal);
        break;
      case "s&l":
        sessionStorage.setItem(formattedKey, formattedVal);
        localStorage.setItem(formattedKey, formattedVal);
        break;
    }
  }

  get(key: string, customScope = this.scope, mode: StorageMode = this.mode) {
    this.log(`get: ${key}`);
    const formattedKey = this.keyFormatter(key, customScope);
    switch (mode) {
      case "session":
        return this.valueFormatter(sessionStorage.getItem(formattedKey), true);
      case "local":
        return this.valueFormatter(localStorage.getItem(formattedKey), true);
      case "s&l":
        return this.valueFormatter(
          sessionStorage.getItem(formattedKey) || localStorage.getItem(formattedKey),
          true,
        );
    }
  }

  getItems(keys: string[], customScope = this.scope, customMode = this.mode) {
    switch (customMode) {
      case "session":
        return keys.reduce<Dictionary>((res, key) => {
          const formattedKey = this.keyFormatter(key, customScope);
          const value = this.valueFormatter(sessionStorage.getItem(formattedKey), true);
          if (value) res[key] = value;
          return res;
        }, {});
      case "local":
        return keys.reduce<Dictionary>((res, key) => {
          const formattedKey = this.keyFormatter(key, customScope);
          const value = this.valueFormatter(localStorage.getItem(formattedKey), true);
          if (value) res[key] = value;
          return res;
        }, {});
      case "s&l": {
        const sessionValues = keys.reduce<Dictionary>((res, key) => {
          const formattedKey = this.keyFormatter(key, customScope);
          const value = this.valueFormatter(sessionStorage.getItem(formattedKey), true);
          if (value) res[key] = value;
          return res;
        }, {});

        const localValues = keys.reduce<Dictionary>((res, key) => {
          const formattedKey = this.keyFormatter(key, customScope);
          const value = this.valueFormatter(localStorage.getItem(formattedKey), true);
          if (value) res[key] = value;
          return res;
        }, {});

        const useSessionValues = Object.values(sessionValues).filter((v) => !!v).length > 0;
        console.log("useSessionValues: ", useSessionValues);
        return useSessionValues ? sessionValues : localValues;
      }
    }
  }

  remove(key: string, customScope = this.scope) {
    this.log(`remove: ${key}`);
    const formattedKey = this.keyFormatter(key, customScope);
    switch (this.mode) {
      case "session":
        sessionStorage.removeItem(formattedKey);
        break;
      case "local":
        localStorage.removeItem(formattedKey);
        break;
      case "s&l":
        sessionStorage.removeItem(formattedKey);
        localStorage.removeItem(formattedKey);
        break;
    }
  }

  clean() {
    this.log(`clean`);
    switch (this.mode) {
      case "session":
        sessionStorage.clear();
        break;
      case "local":
        localStorage.clear();
        break;
      case "s&l":
        sessionStorage.clear();
        localStorage.clear();
        break;
    }
  }
}

export const cloudStorage = new Storage({
  mode: "s&l",
  name: "cloudStorage",
});
